<template>
  <div
    class="mc-video-history-new__video-section-wrapper"
    :class="{
      'grid-1': gridNum === 1,
      'grid-2': gridNum === 2,
      'grid-4': gridNum === 4,
      'grid-6': gridNum === 6,
      'grid-8': gridNum === 8,
      'grid-9': gridNum === 9,
      'grid-16': gridNum === 16,
    }"
  >
    <template v-if="!refreshVideoChannels">
      <VideoItem
        v-for="channel in normalizedChannels"
        v-show="calculatedVideoChannels.includes(channel)"
        :ref="`videoItemRef-${channel}`"
        :key="`video-channel-${channel}`"
        :channel="channel"
        :state="mapWebviewStatePerChannel[channel] ?? ''"
        :imei="imei"
        @clickPlay="clickPlay"
        @togglePaused="togglePaused"
      />
      <NoVideo
        v-for="n in calculatedGridEmpty"
        :key="`no-video-${n}`"
        :label="
          calculatedVideoChannels.length > 0
            ? $t('trackVideo.state.noVideo')
            : $t('videoHistory.selectCameraFirst')
        "
      />
    </template>
  </div>
</template>

<script>
import NoVideo from './NoVideo.vue'
import { mapState, mapGetters } from 'vuex'
import VideoItem from './VideoItem.vue'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import { getPlaybackSyncDecision, getProjectedPlaybackTime } from '../../helper/playback-time-sync'
moment.locale('id')

export default {
  components: {
    VideoItem,
    NoVideo,
  },
  mixins: [Helper],
  data() {
    return {
      calculatedVideoChannels: [],
      refreshVideoChannels: false,
      intervalCheckTimeSync: null,

      // Seberapa sering sinkronisasi antar-channel diperiksa.
      timeSyncCheckIntervalMs: 300,

      // Pause channel jika projected time-nya unggul setidaknya sebesar nilai ini
      // dari channel dengan projected time paling lambat.
      timeSyncPauseThresholdMs: 300,

      // Resume channel yang dipause saat channel yang berjalan sudah mendekat
      // hingga selisih maksimum ini. Nilainya harus lebih kecil dari pause threshold.
      timeSyncResumeThresholdMs: 20,

      // Durasi minimum sebuah channel harus tetap hidden-pause sebelum boleh di-resume.
      minimumHiddenPauseDurationMs: 600,

      // Jarak minimum antar-batch command pause/resume untuk mencegah command berulang cepat.
      timeSyncActionCooldownMs: 500,

      webviewTimingSamples: {},
      hiddenPauseStartedAtPerChannel: {},
      lastTimeSyncActionAt: -Infinity,
      isAllSync: true,
    }
  },
  computed: {
    ...mapState('videoHistory', [
      'mapWebviewStatePerChannel',
      'vehicleStatuses',
      'vehicleIdActive',
      'videoChannelActive',
      'mapFilelist',
      'mapWebviewDatetimePerChannel',
      'mapWebviewTimestampPerChannel',
      'mapWebviewHiddenPausedActivePerChannel',
      'hasIntentToTimeChange',
      'activeTab',
      'filelistPlaySelected',
    ]),
    ...mapGetters('videoHistory', [
      'markerTimelineDate',
      'deviceTimezone',
      'normalizedChannels',
      'isThereIsWebviewActive',
      'isMultiChannelActive',
    ]),
    imei() {
      return this.vehicleStatuses[this.vehicleIdActive]?.vehicle?.imei ?? ''
    },
    gridNum() {
      const channelActive = this.calculatedVideoChannels.length
      if (channelActive === 0) return 1
      if (channelActive === 1) return 1
      if (channelActive === 2) return 2
      if (channelActive <= 4) return 4
      if (channelActive <= 6) return 6
      if (channelActive <= 8) return 8
      if (channelActive <= 9) return 9
      if (channelActive <= 16) return 16
      return 1
    },
    calculatedGridEmpty() {
      return this.gridNum - this.calculatedVideoChannels.length
    },
  },

  watch: {
    isMultiChannelActive(isMultiChannelActive) {
      clearInterval(this.intervalCheckTimeSync)

      if (!isMultiChannelActive) {
        this.resetTimeSync(true)
        return
      }

      this.intervalCheckTimeSync = setInterval(() => {
        this.checkTimeSync()
      }, this.timeSyncCheckIntervalMs)
    },
    markerTimelineDate(markerTimelineDate) {
      this.videoChannelActive.forEach((channel) => {
        const filelist = this.mapFilelist[channel]
        if (!filelist || filelist.length === 0) return

        const isThereFilelist = filelist.some((f) => {
          const utcStartTime = this.getUtcDateFromLocalDate(f.startTime, this.deviceTimezone)
          const utcEndTime = this.getUtcDateFromLocalDate(f.endTime, this.deviceTimezone)
          return (
            this.isBetweenDate(markerTimelineDate, utcStartTime, utcEndTime) ||
            new Date(markerTimelineDate).getTime() === new Date(utcStartTime).getTime()
          )
        })

        if (isThereFilelist && this.mapWebviewStatePerChannel[channel] === null) {
          const isThereIsPlay = Object.values(this.mapWebviewStatePerChannel).some(
            (state) =>
              state === 'CONNECTING' ||
              state === 'PLAYING' ||
              state === 'FREEZE' ||
              state === 'LOAD'
          )
          if (isThereIsPlay) {
            this.$store.commit('videoHistory/setMapWebviewStatePerChannel', {
              channel,
              state: 'LOAD',
            })
          } else {
            this.$store.commit('videoHistory/setMapWebviewStatePerChannel', {
              channel,
              state: 'NOT_STARTED',
            })
          }
        }
      })
    },
    '$store.state.videoHistory.videoChannelActive': {
      handler(channelActive, oldChannelActive) {
        if (channelActive.length > oldChannelActive?.length) {
          const newChannels = channelActive.filter((ch) => !oldChannelActive.includes(ch))
          newChannels.forEach((ch) => this.handleAddVideoChannel(ch))
        } else if (oldChannelActive.length > channelActive.length) {
          const removedChannels = oldChannelActive.filter((ch) => !channelActive.includes(ch))
          removedChannels.forEach((ch) => {
            this.handleRemoveVideoChannel(ch)
            this.$delete(this.webviewTimingSamples, ch)
            this.$delete(this.hiddenPauseStartedAtPerChannel, ch)
            this.$store.commit('videoHistory/setMapWebviewHiddenPausedActivePerChannel', {
              channel: ch,
              isHiddenPausedActive: false,
            })
            this.$store.commit('videoHistory/deleteMapWebviewBufferedTimePerChannel', ch)
          })
        }
      },
      immediate: true,
    },
    hasIntentToTimeChange: {
      async handler(hasIntentToTimeChange) {
        if (hasIntentToTimeChange) {
          this.resetTimeSync(false)
          this.refreshVideoChannels = true
          await this.$nextTick()
          // await this.delay(100)
          this.refreshVideoChannels = false
          // this.isAllSync = false

          await this.$nextTick()
        }
      },
    },
    activeTab(activeTab, oldActiveTab) {
      if (this.isThereIsWebviewActive) {
        this.videoChannelActive.forEach((ch) => {
          this.$store.dispatch('videoHistory/removeChannelToGridWebview', ch)
        })
      }

      if (
        oldActiveTab === 'filelist' &&
        activeTab === 'timeline' &&
        this.filelistPlaySelected?.mediaFilename
      ) {
        this.$store.commit('videoHistory/resetFilelistPlaySelected')
      }
    },
  },

  mounted() {
    window.addEventListener('message', this.handleMessage)
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage)
    clearInterval(this.intervalCheckTimeSync)
  },

  methods: {
    clickPlay() {
      const channelActive = this.videoChannelActive

      if (channelActive.length === 0) return

      channelActive.forEach((ch) => {
        const isThereFilelist = this.mapFilelist[ch]?.some((fl) => {
          const utcStart = this.getUtcDateFromLocalDate(fl.startTime, this.deviceTimezone)
          const utcEnd = this.getUtcDateFromLocalDate(fl.endTime, this.deviceTimezone)
          return (
            this.isBetweenDate(this.markerTimelineDate, utcStart, utcEnd) ||
            utcStart === this.markerTimelineDate
          )
        })
        if (!isThereFilelist) return
        this.$store.dispatch('videoHistory/playChannel', ch)
      })
    },

    postToIframeHiddenPaused(channel, isHiddenPaused) {
      this.$store.dispatch('videoHistory/postToStreamIframe', {
        channel: channel,
        imei: this.imei,
        payload: {
          type: 'VIDEO_HISTORY_TOGGLE_HIDDEN_PAUSED',
          data: {
            channel: channel,
            imei: this.imei,
            isHiddenPaused: isHiddenPaused,
          },
        },
      })
    },
    getTimeSyncNow() {
      return window.performance?.now?.() ?? Date.now()
    },
    rebaseTimingSample(channel, isPaused, now) {
      const sample = this.webviewTimingSamples[channel]
      const playbackTimestamp = getProjectedPlaybackTime(sample, isPaused, now)

      if (playbackTimestamp === null) return

      this.$set(this.webviewTimingSamples, channel, {
        playbackTimestamp,
        receivedAt: now,
      })
    },
    setChannelHiddenPaused(channel, isHiddenPaused, now) {
      const wasHiddenPaused = Boolean(this.mapWebviewHiddenPausedActivePerChannel[channel])

      this.rebaseTimingSample(channel, wasHiddenPaused, now)
      this.postToIframeHiddenPaused(channel, isHiddenPaused)
      this.$store.commit('videoHistory/setMapWebviewHiddenPausedActivePerChannel', {
        channel,
        isHiddenPausedActive: isHiddenPaused,
      })

      if (isHiddenPaused) {
        this.$set(this.hiddenPauseStartedAtPerChannel, channel, now)
      } else {
        this.$delete(this.hiddenPauseStartedAtPerChannel, channel)
      }
    },
    resetTimeSync(resumePausedChannels) {
      if (resumePausedChannels) {
        const now = this.getTimeSyncNow()

        this.videoChannelActive.forEach((channel) => {
          if (!this.mapWebviewHiddenPausedActivePerChannel[channel]) return
          this.setChannelHiddenPaused(channel, false, now)
        })
      }

      if (!resumePausedChannels) {
        this.$store.commit('videoHistory/resetMapWebviewHiddenPausedActivePerChannel')
      }

      this.webviewTimingSamples = {}
      this.hiddenPauseStartedAtPerChannel = {}
      this.lastTimeSyncActionAt = -Infinity
      this.isAllSync = true
    },
    checkTimeSync() {
      const now = this.getTimeSyncNow()

      const syncChannels = this.videoChannelActive.filter(
        (channel) => this.mapWebviewStatePerChannel[channel] !== 'NOT_STARTED'
      )

      const areAllChannelsReady = syncChannels.every((channel) => {
        const state = this.mapWebviewStatePerChannel[channel]
        const sample = this.webviewTimingSamples[channel]

        return (
          (state === 'PLAYING' || state === 'FREEZE') &&
          Number.isFinite(sample?.playbackTimestamp) &&
          Number.isFinite(sample?.receivedAt)
        )
      })

      if (!areAllChannelsReady) {
        this.isWaitingForChannelsReady = true
        syncChannels.forEach((channel) => {
          const state = this.mapWebviewStatePerChannel[channel]
          const hasSample = Number.isFinite(this.webviewTimingSamples[channel]?.playbackTimestamp)
          const isPaused = this.mapWebviewHiddenPausedActivePerChannel[channel]

          if ((state === 'PLAYING' || state === 'FREEZE') && hasSample && !isPaused) {
            this.setChannelHiddenPaused(channel, true, now)
          }
        })

        return
      }

      if (this.isWaitingForChannelsReady) {
        syncChannels.forEach((channel) => {
          if (!this.mapWebviewHiddenPausedActivePerChannel[channel]) return
          this.setChannelHiddenPaused(channel, false, now)
        })

        this.isWaitingForChannelsReady = false
        this.lastTimeSyncActionAt = now
        return
      }

      const decision = getPlaybackSyncDecision({
        channels: syncChannels,
        samples: this.webviewTimingSamples,
        pausedChannels: this.mapWebviewHiddenPausedActivePerChannel,
        pausedAtPerChannel: this.hiddenPauseStartedAtPerChannel,
        now,
        pauseThresholdMs: this.timeSyncPauseThresholdMs,
        resumeThresholdMs: this.timeSyncResumeThresholdMs,
        minimumPauseDurationMs: this.minimumHiddenPauseDurationMs,
      })
      const channelsToChange = [...decision.channelsToPause, ...decision.channelsToResume]

      this.isAllSync =
        decision.channelsToPause.length === 0 &&
        !syncChannels.some((channel) =>
          Boolean(this.mapWebviewHiddenPausedActivePerChannel[channel])
        )

      if (channelsToChange.length === 0) return
      if (now - this.lastTimeSyncActionAt < this.timeSyncActionCooldownMs) return

      decision.channelsToPause.forEach((channel) => {
        this.setChannelHiddenPaused(channel, true, now)
      })
      decision.channelsToResume.forEach((channel) => {
        this.setChannelHiddenPaused(channel, false, now)
      })

      this.lastTimeSyncActionAt = now
    },
    handleMessage(event) {
      if (!this.isThereIsWebviewActive) return
      try {
        const payload = typeof event.data === 'string' ? JSON.parse(event.data) : ''
        const _type = payload?.type,
          _data = payload?.data
        if (!(this.imei === _data?.imei)) return
        if (_type === 'VIDEO_HISTORY_STATE_CHANGED') {
          this.$store.commit('videoHistory/setMapWebviewStatePerChannel', {
            channel: _data.channel,
            state: _data.state,
          })
        }
        if (_type === 'VIDEO_BUFFER_INFO') {
          const playbackTimestamp = Date.parse(_data.currentPlaybackDatetime)

          if (Number.isFinite(playbackTimestamp)) {
            this.$set(this.webviewTimingSamples, _data.channel, {
              playbackTimestamp,
              receivedAt: this.getTimeSyncNow(),
            })
          }

          this.$store.commit('videoHistory/setMapWebviewBufferedTimePerChannel', {
            channel: _data.channel,
            start: _data.cacheStartDatetime,
            end: _data.cacheEndDatetime,
          })
          this.$store.commit('videoHistory/setMapWebviewDatetimePerChannel', {
            channel: _data.channel,
            datetime: _data.currentPlaybackDatetime,
          })
          this.$store.commit('videoHistory/setMapWebviewTimestampPerChannel', {
            channel: _data.channel,
            timestamp: _data.currentPlaybackTimestampMs,
          })
        }
      } catch (error) {
        console.error(error)
      }
    },
    handleAddVideoChannel(channel) {
      this.calculatedVideoChannels.push(channel)
    },
    handleRemoveVideoChannel(channel) {
      const index = this.calculatedVideoChannels.indexOf(channel)
      if (index !== -1) {
        this.calculatedVideoChannels.splice(index, 1)
      }
    },
    togglePaused(isPaused) {
      this.calculatedVideoChannels.forEach((channel) => {
        const refValue = this.$refs[`videoItemRef-${channel}`]
        const videoItemRef = Array.isArray(refValue) ? refValue[0] : refValue

        if (videoItemRef) {
          videoItemRef.setIsPaused(isPaused)
          videoItemRef.postMessageTogglePausedToIframe(isPaused)
        }
      })
    },
  },
}
</script>
